import { HEALTH_METRICS, validHealthDay, healthDay } from "./healthStatistics"
import { BODY_METRICS } from "./mescanHealthData"

const methods = {
  daily_total: "Tagessumme", daily_mean: "Tagesdurchschnitt", sleep_window: "Schlafzeit · 18 bis 18 Uhr",
  latest: "Letzte Einzelmessung", mescan_report: "MeScan-Studiomessung", not_reported: "Messmethode nicht übermittelt",
}
const number = value => typeof value === "number" && Number.isFinite(value) && value >= 0
const metadata = metric => HEALTH_METRICS.find(item => item.key === metric) || BODY_METRICS.find(item => item.key === metric)
const unit = metric => metric === "distanceMeters" ? "m" : metric === "sleepSeconds" ? "s" : metadata(metric)?.unit || ""
const dailyMethod = metric => ["heartRate", "weightKilograms"].includes(metric) ? "daily_mean" : metric === "sleepSeconds" ? "sleep_window" : "daily_total"

// Keep source, device, metric and measurement method as independent series.
// In particular: daily pulse != last pulse != resting pulse != pulse at MeScan;
// active energy != total energy, and lean mass != muscle mass.
export function customerHealthSeries({ histories = [], snapshot = null, records = [], scans = [] } = {}) {
  const series = new Map(), seen = new Set()
  const add = (item) => {
    const description = metadata(item.metric)
    if (!description || !number(item.value) || !validHealthDay(item.day) ||
      (["heartRate", "restingHeartRate", "weightKilograms"].includes(item.metric) && item.value === 0)) return
    const key = JSON.stringify([item.metric, item.source, item.device || "", item.method])
    const unique = `${key}:${item.id || item.time || item.day}`
    if (seen.has(unique)) return
    seen.add(unique)
    if (!series.has(key)) series.set(key, { key, metric: item.metric, label: description.label, unit: unit(item.metric),
      source: item.source, sourceName: item.sourceName, device: item.device || "", method: item.method,
      methodLabel: methods[item.method] || methods.not_reported, points: [] })
    series.get(key).points.push({ ...item, time: number(item.time) ? item.time : null })
  }
  for (const history of histories) {
    for (const day of history?.days || []) {
      if (!day.readable || day.future) continue
      for (const metric of HEALTH_METRICS) add({ metric: metric.key, value: day.values?.[metric.key], day: day.day,
        source: "health_connect", sourceName: "Health Connect", method: dailyMethod(metric.key), partial: day.partial === true })
    }
  }
  if (snapshot) for (const metric of HEALTH_METRICS) {
    const individual = ["heartRate", "weightKilograms"].includes(metric.key)
    const time = snapshot.daily?.[metric.key === "heartRate" ? "heartRateMeasuredAt" : "weightMeasuredAt"]
    add({ metric: metric.key, value: snapshot.daily?.[metric.key], day: individual && number(time) ? healthDay(new Date(time)) : snapshot.day,
      time: individual ? time : null, source: "health_connect", sourceName: "Health Connect",
      method: individual ? "latest" : dailyMethod(metric.key), partial: !individual })
  }
  for (const record of records) {
    if (!number(record.time)) continue
    add({ ...record, day: healthDay(new Date(record.time)), method: "not_reported" })
  }
  for (const scan of scans) {
    if (!number(scan.time) || scan.ambiguous) continue
    for (const [metric, value] of Object.entries(scan.values || {})) add({ metric, value, id: scan.id, time: scan.time,
      day: healthDay(new Date(scan.time)), source: "mescan", sourceName: "MeScan", method: "mescan_report" })
  }
  return [...series.values()].map(item => ({ ...item,
    points: item.points.sort((a, b) => a.day.localeCompare(b.day) || (a.time || 0) - (b.time || 0)),
  })).map(item => ({ ...item, latest: item.points[item.points.length - 1] }))
}

export function customerHealthRange(from, to, now = new Date()) {
  if (!validHealthDay(from) || !validHealthDay(to) || from > to || from < "2000-01-01") throw new Error("Bitte einen gültigen Zeitraum auswählen.")
  const today = healthDay(now)
  return { from, to: to > today ? today : to, requestedTo: to, today, futureOnly: from > today }
}
